import { useAccordionControl } from 'components/accordion/useAccordionControl'
import { FieldStatusEnum, PecFormApi, resolveName, useListFieldStatus } from 'components/form'
import { TipoAtendimentoEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { useMemo, useState } from 'react'
import { useField } from 'react-final-form'
import { MetaArray, metaPath } from 'util/metaPath'
import { SoapState } from 'view/atendimentos/atendimento-individual/model'
import { useEditableListField } from 'view/atendimentos/detail/components/EditableList'
import { AtendimentoProfissional } from 'view/atendimentos/types/AtendimentoProfissionalModel'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'

import HeadingFormControl from '../components/HeadingFormControl'
import { OrientacoesCadastroView } from './list/OrientacoesCadastroView'
import { OrientacoesTable } from './list/OrientacoesTable'
import { OrientacaoFormModel } from './model-orientacoes'

interface OrientacoesViewProps {
  name: MetaArray<OrientacaoFormModel>
  prontuarioId: ID
  atendimentoId: ID
  cidadao: CidadaoAtendimento
  orientacoes: OrientacaoFormModel[] | null
  atendimentoProfissional: AtendimentoProfissional
}

const metaSoap = metaPath<SoapState>()

export function OrientacoesView(props: Readonly<OrientacoesViewProps>) {
  const { name, prontuarioId, atendimentoId, orientacoes, atendimentoProfissional, cidadao } = props

  const {
    handleSubmit: handleSubmitEditableList,
    removeItem,
    resetItemToInitialValue,
    handleRowChanged,
  } = useEditableListField({
    name,
  })

  const [idOrientacaoEmEdicao, setIdOrientacaoEmEdicao] = useState<ID>()

  const { resetExpandedItems, ...accordionProps } = useAccordionControl({})

  const {
    observacao: { isAtendimentoObservacao },
    isRetificacao,
  } = useAtendimentoContext()

  const { fieldStatus } = useListFieldStatus({
    name: name,
    controlModifiedStatus: isRetificacao,
    isSameRow,
  })

  const orientacoesWithStatus = useMemo(
    () =>
      isRetificacao ? fieldStatus : new Map(orientacoes?.map((orientacao) => [orientacao, FieldStatusEnum.ADICIONADO])),
    [fieldStatus, isRetificacao, orientacoes]
  )

  const {
    input: { value: tipoAtendimentoValue },
  } = useField<TipoAtendimentoEnum>(resolveName(metaSoap.finalizacao.tipoAtendimento), {
    subscription: { value: true },
  })

  const handleSubmit = (value: OrientacaoFormModel, formApi: PecFormApi<OrientacaoFormModel>) => {
    handleSubmitEditableList(value, formApi)
    resetExpandedItems()
  }

  const handleRemove = (itemToRemove: OrientacaoFormModel) => {
    removeItem(itemToRemove)
    resetExpandedItems()
  }

  return (
    <>
      <HeadingFormControl label='Orientações ao cidadão' required level='h4' />
      <OrientacoesCadastroView onSubmit={handleSubmit} onFocus={() => setIdOrientacaoEmEdicao(null)} />
      <OrientacoesTable
        cidadao={cidadao}
        atendimentoId={atendimentoId}
        prontuarioId={prontuarioId}
        orientacoesWithStatus={orientacoesWithStatus}
        atendimentoProfissionalId={atendimentoProfissional.id}
        tipoAtendimento={tipoAtendimentoValue}
        localAtendimento={atendimentoProfissional.atendimento?.localAtendimento}
        isAtendimentoObservacao={isAtendimentoObservacao}
        dataReferencia={atendimentoProfissional.iniciadoEm}
        lotacao={atendimentoProfissional.lotacao}
        idOrientacaoEmEdicao={idOrientacaoEmEdicao}
        onUpdateItem={handleRowChanged}
        onRemoveItem={handleRemove}
        onEditClick={setIdOrientacaoEmEdicao}
        resetExpandedItems={resetExpandedItems}
        isRetificacao={isRetificacao}
        onResetItem={resetItemToInitialValue}
        {...accordionProps}
      />
    </>
  )
}

const isSameRow = (a: OrientacaoFormModel, b: OrientacaoFormModel) => a._id === b._id
